import {
  mergeTyped,
  useDataStream,
  useNavigator,
  useStreamValue,
  useVoidEventAsStream
} from 'annotation-tools/utils';
import React, { FC, memo, useCallback, useMemo } from 'react';
import { mapTo } from 'rxjs/operators';
import { createModel } from '../../../api/fetch-functions/model';
import { ModelAPIRequestBodyType } from '../../../api/types/model/api-request-body';
import { maxStringLength } from '../../../constants/max-string-length';
import { viewTexts } from '../../../constants/view-texts';
import { useFetchResultState } from '../../../functions/hooks/use-fetch-result-state';
import {
  FormInputStateConfigType,
  useFormInputState
} from '../../../functions/hooks/use-form-input-state';
import { useToken, useUserDetail } from '../../../global-state/auth-state';
import { fetchModelRelatedList } from '../../../global-state/database/combined';
import { fetchModelListOf } from '../../../global-state/database/model-list';
import { koumutenId$ } from '../../../global-state/router-id';
import { routes } from '../../../routing/routes';
import { IModelType } from '../../../types/record/model';
import { CreateNewModelViewDialog } from './create-new-model-dialog-view';

const formInputStateConfig: FormInputStateConfigType<ModelAPIRequestBodyType> = {
  minStringLength: 1,
  maxStringLength: maxStringLength.modelName,
  stringLengthCheckMessageTargetName:
    viewTexts.stringLengthCheckMessageTargetName.inferenceModel,
  required: true,
  validationErrorContentMapFn: content =>
    content.name !== undefined ? content.name.toArray() : []
};

interface Props {
  open: boolean;
  onClose: (reason: 'ok' | 'cancel' | '') => void;
  companyId: number;
}

export const CreateNewModelDialog: FC<Props> = memo(
  ({ open, onClose, companyId }: Readonly<Props>) => {
    const token = useToken();
    const { name } = useUserDetail();

    /* state */

    const [
      // dummy comment
      fetchResult,
      setFetchResult
    ] = useFetchResultState<IModelType, ModelAPIRequestBodyType>();

    const nonFieldErrors = useMemo<string[]>(
      () =>
        fetchResult !== undefined &&
        fetchResult.type === 'error' &&
        fetchResult.body.type === 'non-field-errors'
          ? fetchResult.body.content.toArray()
          : [],
      [fetchResult]
    );

    const [
      modelName$,
      modelNameErrors$,
      modelNameIsValid$,
      modelNameOnInput
    ] = useFormInputState<IModelType, ModelAPIRequestBodyType>(
      open,
      fetchResult,
      formInputStateConfig
    );

    const [submitOnClick$, submitOnClick] = useVoidEventAsStream();

    const submitButtonEnabled$ = useDataStream<boolean>(
      false,
      mergeTyped(submitOnClick$.pipe(mapTo(false)), modelNameIsValid$)
    );

    /* extract values */

    const submitButtonEnabled = useStreamValue(submitButtonEnabled$, false);

    const modelName = useStreamValue(modelName$);
    const modelNameErrors = useStreamValue(modelNameErrors$);

    /* event handlers */

    const koumutenId = useStreamValue(koumutenId$);
    const navigator = useNavigator();

    const closeOnClick = useCallback(
      (reason: '' | 'cancel' | 'ok') => {
        if (reason !== 'ok') {
          onClose(reason);
        } else {
          submitOnClick();
          if (
            token !== undefined &&
            koumutenId !== undefined &&
            modelName !== undefined
          ) {
            createModel(token)(companyId)({
              name: modelName,
              companyId,
              adoptedVersionId: undefined
            }).then(res => {
              setFetchResult(res);
              if (res.type === 'success') {
                onClose(reason);
                fetchModelListOf(companyId);
                const modelId = res.body.id;
                fetchModelRelatedList(modelId);
                navigator(routes.modelRoot(koumutenId, companyId, modelId));
              }
            });
          }
        }
      },
      [
        onClose,
        navigator,
        koumutenId,
        companyId,
        modelName,
        token,
        setFetchResult,
        submitOnClick
      ]
    );

    return (
      <CreateNewModelViewDialog
        open={open}
        onClose={closeOnClick}
        registererName={name}
        modelName={modelName}
        modelNameErrors={modelNameErrors}
        modelNameOnInput={modelNameOnInput}
        submitButtonEnabled={submitButtonEnabled}
        nonFieldErrors={nonFieldErrors}
      />
    );
  }
);

CreateNewModelDialog.displayName = 'CreateNewModelDialog';
